import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bell,
  Building2,
  Clock,
  Lock,
  MessageSquare,
  Printer,
  RefreshCw,
  Shield,
  Store,
  Warehouse,
  Banknote,
} from 'lucide-react';
import CaissePostesSettings from './CaissePostesSettings.jsx';
import StripeConnectPanel from '../StripeConnectPanel';
import SoundPreferencesControls from './SoundPreferencesControls';
import {
  ComingSoonBadge,
  SettingsCard,
  SettingsLavenderGroup,
  SettingsPageHeader,
  SettingsReadOnlyField,
  SettingsSwitch,
  SettingsTextField,
} from './settingsUi.jsx';
import {
  formatEstablishmentAddress,
  formatRoleLabel,
  getSessionEmail,
} from '../../utils/settingsSessionUser.js';
import { NAV_HELP_MAILTO } from '../../config/nav/resolveCommercantNav.js';
import { getAssistanceCommercantId } from '../../utils/assistanceSession';
import { useAdminSessionUser } from '../../hooks/useAdminSessionUser.js';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../Toast';

const BASE_TABS = [
  { id: 'etablissement', label: 'Mon établissement', icon: Building2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'impression', label: 'Impression', icon: Printer },
  { id: 'securite', label: 'Compte & sécurité', icon: Shield },
];

function canEditClickoricoIdentity(isSuperAdmin, commercantId) {
  if (!isSuperAdmin || !commercantId) return false;
  return String(getAssistanceCommercantId() || '') === String(commercantId);
}

function formatSiretDisplay(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length !== 14) return digits;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
}

function LegalSiretField({ commercant, commercantId, onCommercantUpdate }) {
  const { toasts, toast, removeToast } = useToast();
  const [siret, setSiret] = useState(formatSiretDisplay(commercant?.siret || ''));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSiret(formatSiretDisplay(commercant?.siret || ''));
  }, [commercant?.siret]);

  const saveSiret = async () => {
    const digits = String(siret || '').replace(/\D/g, '');
    if (digits && digits.length !== 14) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres.');
      return;
    }
    setSaving(true);
    try {
      const response = await apiService.updateCommercant(commercantId, {
        siret: digits || null,
      });
      const updated = response?.commercant || response || {};
      onCommercantUpdate?.({ siret: digits || null, ...updated });
      toast.success('SIRET enregistré');
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Impossible d’enregistrer le SIRET');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
      <SettingsTextField
        label="SIRET"
        compact
        value={siret}
        onChange={setSiret}
      />
      <p className="text-xs text-gray-500">
        Numéro SIRET à 14 chiffres de l’établissement.
      </p>
      <button
        type="button"
        disabled={saving}
        onClick={saveSiret}
        className="inline-flex items-center rounded-xl bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {saving ? 'Enregistrement…' : 'Enregistrer le SIRET'}
      </button>
    </div>
  );
}

function ClientMessagesCard({ commercant, commercantId, onCommercantUpdate }) {
  const { toasts, toast, removeToast } = useToast();
  const [livraison, setLivraison] = useState(commercant?.messageLivraisonClient || '');
  const [clickCollect, setClickCollect] = useState(commercant?.messageClickCollectClient || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLivraison(commercant?.messageLivraisonClient || '');
    setClickCollect(commercant?.messageClickCollectClient || '');
  }, [commercant?.messageLivraisonClient, commercant?.messageClickCollectClient]);

  const saveMessages = async () => {
    setSaving(true);
    try {
      const payload = {
        messageLivraisonClient: livraison.trim() || null,
        messageClickCollectClient: clickCollect.trim() || null,
      };
      const response = await apiService.updateCommercant(commercantId, payload);
      const updated = response?.commercant || response || {};
      onCommercantUpdate?.({ ...payload, ...updated });
      toast.success('Messages clients enregistrés');
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Impossible d’enregistrer les messages');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 space-y-3 border-t border-gray-100 pt-4">
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
        <MessageSquare className="h-3.5 w-3.5" />
        Messages clients
      </p>
      <p className="text-xs text-gray-500">
        Ce message sera affiché au client dans le suivi de sa commande.
      </p>
      <label className="block">
        <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Message livraison</span>
        <textarea
          value={livraison}
          onChange={(e) => setLivraison(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
        />
      </label>
      <label className="block">
        <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Message Click & Collect</span>
        <textarea
          value={clickCollect}
          onChange={(e) => setClickCollect(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
        />
      </label>
      <button
        type="button"
        disabled={saving || !commercantId}
        onClick={saveMessages}
        className="inline-flex items-center rounded-xl bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {saving ? 'Enregistrement…' : 'Enregistrer les messages'}
      </button>
    </div>
  );
}

function EstablishmentCard({
  commercant,
  commercantId,
  canEditIdentity = false,
  showSessionIdentity = true,
  onOpenEstablishment,
  onOpenHours,
  onCommercantUpdate,
}) {
  const address = formatEstablishmentAddress(commercant);
  const session = useAdminSessionUser();
  const loginEmail = getSessionEmail(session);
  const roleLabel = formatRoleLabel(session?.role);
  const { toasts, toast, removeToast } = useToast();
  const [nom, setNom] = useState(commercant?.nom || '');
  const [savingNom, setSavingNom] = useState(false);

  useEffect(() => {
    setNom(commercant?.nom || '');
  }, [commercant?.nom]);

  const saveNom = async () => {
    const trimmed = String(nom || '').trim();
    if (!trimmed) {
      toast.error('Le nom de l’établissement est obligatoire.');
      return;
    }
    setSavingNom(true);
    try {
      const response = await apiService.updateCommercant(commercantId, { nom: trimmed });
      const updated = response?.commercant || response || {};
      onCommercantUpdate?.({ nom: trimmed, ...updated });
      toast.success('Nom de l’établissement mis à jour');
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Impossible de modifier le nom');
    } finally {
      setSavingNom(false);
    }
  };

  return (
    <SettingsCard
      icon={Building2}
      title="Mon établissement"
      subtitle="Informations générales de votre établissement."
    >
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
      <div className="space-y-3">
        {canEditIdentity ? (
          <div className="space-y-2">
            <SettingsTextField
              label="Nom du commerce"
              compact
              value={nom}
              onChange={setNom}
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={savingNom}
                onClick={() => setNom(commercant?.nom || '')}
                className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={savingNom}
                onClick={saveNom}
                className="inline-flex items-center rounded-xl bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {savingNom ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <SettingsReadOnlyField
              label="Nom du commerce"
              value={commercant?.nom}
              locked
              lockIcon={Lock}
              compact
            />
            <p className="text-xs text-gray-500">Modification gérée par Clickorico</p>
          </>
        )}
        {showSessionIdentity ? (
          <>
            <SettingsReadOnlyField
              label="Email de connexion"
              value={loginEmail}
              locked
              lockIcon={Lock}
              compact
            />
            <p className="text-xs text-gray-500">Adresse utilisée pour la connexion</p>
            <SettingsReadOnlyField
              label="Rôle"
              value={roleLabel}
              locked
              lockIcon={Lock}
              compact
            />
          </>
        ) : null}
      </div>

      <div className="mt-5 space-y-3 border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Informations légales</p>
        <LegalSiretField
          commercant={commercant}
          commercantId={commercantId}
          onCommercantUpdate={onCommercantUpdate}
        />
      </div>

      <div className="mt-5 space-y-3 border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Coordonnées opérationnelles</p>
        <SettingsReadOnlyField label="Téléphone de contact" value={commercant?.telephone} compact />
        <SettingsReadOnlyField label="Email public de contact" value={commercant?.email} compact />
        <SettingsReadOnlyField label="Adresse opérationnelle" value={address} compact />
      </div>

      <ClientMessagesCard
        commercant={commercant}
        commercantId={commercantId}
        onCommercantUpdate={onCommercantUpdate}
      />

      <div className="mt-5 flex flex-col gap-2">
        <button
          type="button"
          onClick={onOpenEstablishment}
          className="text-left text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          Modifier mes coordonnées →
        </button>
        <button
          type="button"
          onClick={onOpenHours}
          className="inline-flex items-center gap-1.5 text-left text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          <Clock className="h-4 w-4" />
          Gérer les horaires →
        </button>
      </div>

      {canEditIdentity ? null : (
        <div className="mt-5 rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-4">
          <p className="text-sm font-medium text-gray-900">Certaines informations sont protégées par Clickorico.</p>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Pour modifier le nom de l'établissement ou une information de compte, contactez l'équipe Clickorico.
          </p>
          <a
            href={NAV_HELP_MAILTO}
            className="mt-3 inline-flex text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            Contacter Clickorico
          </a>
        </div>
      )}
    </SettingsCard>
  );
}

function MerchantNotificationsCard({ commercantId }) {
  return (
    <SettingsCard
      icon={Bell}
      title="Notifications"
      subtitle="Choisissez comment vous êtes notifié."
    >
      <div className="space-y-5">
        <SettingsLavenderGroup title="Nouvelles commandes">
          <SoundPreferencesControls
            commercantId={commercantId}
            showRepeat
            enableLabel="Notifications sonores"
          />
        </SettingsLavenderGroup>
      </div>
    </SettingsCard>
  );
}

function PrintingCard({ isSuperAdmin }) {
  return (
    <SettingsCard
      icon={Printer}
      title="Impression"
      subtitle="Configuration de votre impression."
    >
      <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-gray-900">Impression</p>
          <ComingSoonBadge>En préparation</ComingSoonBadge>
        </div>
        <p className="text-sm leading-6 text-gray-600">
          La gestion quotidienne de l'impression automatique sera bientôt disponible ici.
        </p>
        <p className="mt-3 text-xs leading-5 text-gray-500">
          La configuration technique de l'équipement est gérée par Clickorico.
        </p>
        {isSuperAdmin ? (
          <Link
            to="/materiels"
            className="mt-4 inline-flex text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            Voir le matériel →
          </Link>
        ) : null}
      </div>
    </SettingsCard>
  );
}

function AccountSecurityCard({ showSessionIdentity = true }) {
  const session = useAdminSessionUser();
  const email = getSessionEmail(session);
  const roleLabel = formatRoleLabel(session?.role);

  return (
    <SettingsCard
      icon={Shield}
      title="Compte & sécurité"
      subtitle="Gérez votre compte et la sécurité de votre accès."
    >
      {showSessionIdentity ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <SettingsReadOnlyField label="Email de connexion" value={email} />
          <SettingsReadOnlyField label="Rôle" value={roleLabel} locked lockIcon={Lock} />
        </div>
      ) : null}

      <div className={showSessionIdentity ? 'mt-5' : undefined}>
        <Link
          to="/mot-de-passe-oublie"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          <RefreshCw className="h-4 w-4" />
          Mot de passe oublié / réinitialisation
        </Link>
      </div>
    </SettingsCard>
  );
}

function CommerceOperationsCard({
  commercantId,
  commercant,
  isSuperAdmin,
  onCommercantUpdate,
}) {
  const { toasts, toast, removeToast } = useToast();
  const [savingWeight, setSavingWeight] = useState(false);
  const [savingStock, setSavingStock] = useState(false);
  const stockEnabled = Boolean(commercant?.stock_management_enabled);
  const weightEnabled = Boolean(commercant?.ajustementPoidsReelActif);

  const updateField = async (payload, setBusy) => {
    setBusy(true);
    try {
      const response = await apiService.updateCommercant(commercantId, payload);
      const updated = response?.commercant || response || {};
      onCommercantUpdate?.({ ...payload, ...updated });
      toast.success('Réglage enregistré');
    } catch (err) {
      toast.error(err?.message || 'Impossible de mettre à jour ce paramètre');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsCard
      icon={Warehouse}
      title="Réglages du commerce"
      subtitle="Fonctions déjà disponibles pour le fonctionnement quotidien."
    >
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}

      <div className="space-y-5">
        <StripeConnectPanel
          commercantId={commercantId}
          isSuperAdmin={isSuperAdmin}
        />

        <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-100 p-4">
          <div>
            <p className="font-medium text-gray-900">Ajustement prix au poids</p>
            <p className="mt-1 text-sm text-gray-500">
              Permet de saisir le poids réel à la coupe lors de la préparation et d’ajuster le paiement si besoin (facultatif).
            </p>
          </div>
          <SettingsSwitch
            checked={weightEnabled}
            disabled={savingWeight}
            label="Ajustement prix au poids"
            onChange={(next) => updateField({ ajustementPoidsReelActif: next }, setSavingWeight)}
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-100 p-4">
          <div>
            <p className="font-medium text-gray-900">Décompte du stock produit</p>
            <p className="mt-1 text-sm text-gray-500">
              {stockEnabled
                ? 'Les produits en stock limité voient leur quantité diminuer à chaque vente. Indépendant des ruptures d’ingrédients.'
                : 'Si désactivé, les quantités produit ne sont pas décomptées à la vente (stock illimité). Indépendant des ruptures d’ingrédients.'}
            </p>
          </div>
          <SettingsSwitch
            checked={stockEnabled}
            disabled={savingStock}
            label="Décompte du stock produit"
            onChange={(next) => updateField({ stock_management_enabled: next }, setSavingStock)}
          />
        </div>
      </div>
    </SettingsCard>
  );
}

export default function CommercantSettingsPanel({
  commercantId,
  commercant,
  isSuperAdmin = false,
  onCommercantUpdate,
  onOpenEstablishment,
  onOpenHours,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const canEditIdentity = canEditClickoricoIdentity(isSuperAdmin, commercantId);
  const showSessionIdentity = !isSuperAdmin;
  const moduleCaisseEnabled = Boolean(commercant?.moduleCaisse);
  const showCaissePostesSection = moduleCaisseEnabled || isSuperAdmin;
  const roleInterne = useAdminSessionUser()?.roleInterne;
  const canManageCaissePostes =
    isSuperAdmin || roleInterne === 'GERANT' || roleInterne === 'MANAGER';
  const TABS = useMemo(
    () =>
      showCaissePostesSection
        ? [
            ...BASE_TABS.slice(0, 2),
            { id: 'caisses', label: 'Caisses', icon: Banknote },
            ...BASE_TABS.slice(2),
          ]
        : BASE_TABS,
    [showCaissePostesSection]
  );

  const resolveSettingsTab = (raw) => {
    if (!raw) return 'etablissement';
    return TABS.some((t) => t.id === raw) ? raw : 'etablissement';
  };

  const [activeTab, setActiveTab] = useState(() =>
    resolveSettingsTab(searchParams.get('settingsTab'))
  );

  useEffect(() => {
    const fromUrl = resolveSettingsTab(searchParams.get('settingsTab'));
    if (fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL → onglet merchant
  }, [searchParams, TABS]);

  useEffect(() => {
    if (activeTab === 'caisses' && showCaissePostesSection) {
      window.requestAnimationFrame(() => {
        document.getElementById('settings-merchant-caisses')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [activeTab, showCaissePostesSection]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId === 'etablissement') {
      next.delete('settingsTab');
    } else {
      next.set('settingsTab', tabId);
    }
    setSearchParams(next, { replace: true });
    window.requestAnimationFrame(() => {
      document.getElementById(`settings-merchant-${tabId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  return (
    <div className="settings-page settings-page--merchant p-6" data-settings-chrome="merchant">
      <SettingsPageHeader
        title="Paramètres de l'établissement"
        subtitle="Gérez les préférences et le fonctionnement quotidien de votre établissement."
        kicker="Établissement"
        identity={commercant?.nom || 'Votre établissement'}
        leadingIcon={Store}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <div className="grid grid-cols-1 gap-5">
        <div id="settings-merchant-etablissement" className="scroll-mt-36">
          <EstablishmentCard
            commercant={commercant}
            commercantId={commercantId}
            canEditIdentity={canEditIdentity}
            showSessionIdentity={showSessionIdentity}
            onOpenEstablishment={onOpenEstablishment}
            onOpenHours={onOpenHours}
            onCommercantUpdate={onCommercantUpdate}
          />
        </div>
        <div id="settings-merchant-notifications" className="scroll-mt-36">
          <MerchantNotificationsCard commercantId={commercantId} />
        </div>
        <div id="settings-merchant-impression" className="scroll-mt-36">
          <PrintingCard isSuperAdmin={isSuperAdmin} />
        </div>
        {showCaissePostesSection ? (
          <div id="settings-merchant-caisses" className="scroll-mt-36">
            <CaissePostesSettings
              commercantId={commercantId}
              canManage={canManageCaissePostes}
            />
          </div>
        ) : null}
      </div>

      <div id="settings-merchant-securite" className="mt-5 scroll-mt-36">
        <AccountSecurityCard showSessionIdentity={showSessionIdentity} />
      </div>

      <div className="mt-5">
        <CommerceOperationsCard
          commercantId={commercantId}
          commercant={commercant}
          isSuperAdmin={isSuperAdmin}
          onCommercantUpdate={onCommercantUpdate}
        />
      </div>

      <p className="mt-8 flex items-start justify-center gap-2 text-center text-xs text-gray-400">
        <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px]">i</span>
        Cette page regroupe uniquement les réglages utiles au fonctionnement quotidien de votre établissement.
      </p>
    </div>
  );
}
